import { useState } from "react";
import { useBreakpoint } from "../hooks/useBreakpoint";
import { supabase } from "../lib/supabase";

/* ─── Design tokens (matches BookingsPage / OrderTrackingPage / ProProfilePage) ─── */
const T = {
  green:       "#0F7B3E",
  greenDark:   "#0C6231",
  greenTint:   "#E8F5E9",
  greenBd:     "#C8E6C9",
  white:       "#FFFFFF",
  card2:       "#F8F9FB",
  border:      "#E8EAED",
  borderFaint: "#F0F2F5",
  text:        "#0D1117",
  textMid:     "#374151",
  sub:         "#6B7280",
  faint:       "#9CA3AF",
  red:         "#DC2626",
  redTint:     "#FEF2F2",
  redBd:       "#FECACA",
  amber:       "#D97706",
  amberTint:   "#FFFBEB",
  amberBd:     "#FCD34D",
  font: "'Inter', -apple-system, sans-serif",
  r8: "8px", r10: "10px", r12: "12px", r14: "14px", r16: "16px", r20: "20px", rFull: "999px",
  s4: "0 16px 48px rgba(0,0,0,0.11), 0 4px 12px rgba(0,0,0,0.06)",
};

const TIMES = ["07:00","08:00","09:00","10:00","11:00","12:00","13:00","14:00","15:00","16:00","17:00","18:00"];

const labelSx = {
  margin: "0 0 7px", fontSize: 11, fontWeight: 600, color: T.textMid,
  fontFamily: T.font, textTransform: "uppercase", letterSpacing: "0.07em",
  display: "block",
};

const inputStyle = {
  width: "100%", padding: "11px 14px", borderRadius: T.r10,
  border: `1.5px solid ${T.border}`, fontSize: 14, outline: "none",
  fontFamily: T.font, color: T.text, background: T.white,
  boxSizing: "border-box", minHeight: 44,
};

function fmtClock(t) {
  const [h, m] = t.split(":").map(Number);
  return `${h > 12 ? h - 12 : (h || 12)}:${String(m).padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-KE", { weekday: "long", day: "numeric", month: "long", year: "numeric" }); }
  catch { return iso; }
}

function ErrorBox({ msg }) {
  return (
    <div style={{ background: T.redTint, border: `1px solid ${T.redBd}`, borderRadius: T.r10, padding: "12px 14px", marginBottom: 16 }}>
      <p style={{ margin: 0, fontSize: 13, color: T.red, fontFamily: T.font }}>{msg}</p>
    </div>
  );
}

function Btn({ children, v = "ghost", size = "md", onClick, disabled, full }) {
  const [h, setH] = useState(false);
  const [p, setP] = useState(false);
  const sz = {
    sm: { fs: 12, h: 34, pad: "0 14px", r: T.r8 },
    md: { fs: 13, h: 40, pad: "0 18px", r: T.r10 },
    lg: { fs: 14, h: 48, pad: "0 24px", r: T.r12 },
  }[size];
  const vs = {
    primary: { bg: h ? T.greenDark : T.green, col: "#fff", bd: "none", sh: "0 2px 8px rgba(15,123,62,0.2)" },
    ghost:   { bg: h ? "#F3F5F8" : T.white, col: T.textMid, bd: `1px solid ${T.border}`, sh: "none" },
  };
  const s = vs[v] || vs.ghost;
  return (
    <button
      onClick={onClick} disabled={disabled}
      onMouseEnter={() => setH(true)} onMouseLeave={() => { setH(false); setP(false); }}
      onMouseDown={() => setP(true)} onMouseUp={() => setP(false)}
      style={{
        fontFamily: T.font, fontSize: sz.fs, fontWeight: 500, cursor: disabled ? "default" : "pointer",
        padding: sz.pad, height: sz.h, borderRadius: sz.r, border: s.bd,
        background: s.bg, color: s.col, boxShadow: s.sh,
        display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 7,
        whiteSpace: "nowrap", transition: "all 0.15s ease-out",
        transform: p ? "scale(0.97)" : "scale(1)",
        opacity: disabled ? 0.5 : 1, outline: "none",
        minHeight: 44, width: full ? "100%" : "auto",
      }}>
      {children}
    </button>
  );
}

/**
 * RescheduleBookingModal
 *
 * Props:
 *  - booking: { id, scheduled_time, notes, ... } (the job_request row)
 *  - onClose: () => void
 *  - onRequested: (updatedBooking) => void   called after successful DB update
 *
 * Behaviour:
 *  Sets status = 'reschedule_requested' and stores the customer's proposed
 *  new time in requested_scheduled_time. The original scheduled_time is left
 *  untouched until the pro accepts the new time (handled elsewhere).
 */
export default function RescheduleBookingModal({ booking, onClose, onRequested }) {
  const { isMobile } = useBreakpoint();

  const currentDate = booking.scheduled_time ? booking.scheduled_time.slice(0, 10) : "";
  const currentTime = booking.scheduled_time
    ? new Date(booking.scheduled_time).toLocaleTimeString("en-KE", { hour: "2-digit", minute: "2-digit", hour12: false }).slice(0, 5)
    : "09:00";

  const [date, setDate] = useState("");
  const [time, setTime] = useState(currentTime || "09:00");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const todayStr = new Date().toISOString().split("T")[0];

  const handleConfirm = async () => {
    if (!date) { setError("Please choose a new date."); return; }

    const newIso = new Date(`${date}T${time}`).toISOString();
    if (booking.scheduled_time && newIso === new Date(booking.scheduled_time).toISOString()) {
      setError("That's the same as your current booking time. Please choose a different date or time.");
      return;
    }

    setError(""); setBusy(true);

    const existingNotes = booking.notes ? `${booking.notes}\n\n` : "";
    const newNotes = `${existingNotes}Reschedule requested by customer — proposed ${fmtDate(newIso)} at ${fmtClock(time)}`;

    try {
      const { data, error: err } = await supabase
        .from("job_requests")
        .update({
          status: "reschedule_requested",
          requested_scheduled_time: newIso,
          notes: newNotes,
        })
        .eq("id", booking.id)
        .select()
        .single();

      if (err) throw err;
      onRequested(data);
    } catch (e) {
      console.error("Reschedule request failed:", e);
      setError(e.message || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 900, background: "rgba(13,17,23,0.52)", backdropFilter: "blur(4px)" }} />
      <div style={{
        position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)",
        zIndex: 901,
        width: isMobile ? "calc(100vw - 32px)" : 440,
        maxHeight: "90vh", overflowY: "auto",
        background: T.white, borderRadius: T.r20, boxShadow: T.s4,
        fontFamily: T.font,
      }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", padding: "20px 24px 16px", borderBottom: `1px solid ${T.borderFaint}` }}>
          <div>
            <p style={{ margin: 0, fontSize: 16, fontWeight: 700, color: T.text, fontFamily: T.font }}>Reschedule booking</p>
            <p style={{ margin: "3px 0 0", fontSize: 12, color: T.faint, fontFamily: T.font }}>
              Choose a new date and time for your service
            </p>
          </div>
          <button onClick={onClose} style={{
            width: 32, height: 32, borderRadius: "50%", background: "#f3f4f6",
            border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: T.sub, flexShrink: 0,
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
          </button>
        </div>

        <div style={{ padding: "20px 24px 24px" }}>
          {/* Current booking */}
          <div style={{ background: T.card2, borderRadius: T.r12, border: `1px solid ${T.borderFaint}`, padding: "12px 16px", marginBottom: 18 }}>
            <p style={{ margin: 0, fontSize: 11, fontWeight: 600, color: T.faint, fontFamily: T.font, textTransform: "uppercase", letterSpacing: "0.07em" }}>Currently scheduled</p>
            <p style={{ margin: "4px 0 0", fontSize: 13, fontWeight: 600, color: T.text, fontFamily: T.font }}>
              {booking.scheduled_time ? `${fmtDate(booking.scheduled_time)} at ${fmtClock(currentTime)}` : "Not yet scheduled"}
            </p>
          </div>

          <div style={{ marginBottom: 16 }}>
            <p style={labelSx}>New date</p>
            <input type="date" value={date} min={todayStr} onChange={e => setDate(e.target.value)} style={inputStyle} />
          </div>

          <div style={{ marginBottom: 20 }}>
            <p style={labelSx}>New time</p>
            <select value={time} onChange={e => setTime(e.target.value)} style={inputStyle}>
              {TIMES.map(t => <option key={t} value={t}>{fmtClock(t)}</option>)}
            </select>
          </div>

          <div style={{
            background: T.amberTint, border: `1px solid ${T.amberBd}`,
            borderRadius: T.r10, padding: "11px 14px", marginBottom: 20,
            display: "flex", gap: 9, alignItems: "flex-start",
          }}>
            <span style={{ color: T.amber, flexShrink: 0, display: "flex", marginTop: 1 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            </span>
            <p style={{ margin: 0, fontSize: 12, color: T.amber, fontFamily: T.font, lineHeight: 1.65 }}>
              Your professional needs to confirm the new time. Your original booking stays in place until they accept.
            </p>
          </div>

          {error && <ErrorBox msg={error} />}

          <div style={{ display: "flex", gap: 10 }}>
            <Btn v="ghost" size="md" full onClick={onClose} disabled={busy}>Cancel</Btn>
            <Btn v="primary" size="md" full onClick={handleConfirm} disabled={busy}>
              {busy ? "Sending…" : "Request new time"}
            </Btn>
          </div>
        </div>
      </div>
    </>
  );
}